import { computed, defineComponent, h, ref, unref, watch } from "@vue/composition-api";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import providers from "@/lib/api";
import { parseISO } from "@/lib/time";
import useConsume from "../../hooks/useConsume";
import { usePrivateLiveChat } from "../../hooks/usePrivateLiveChat";
import { usePrivateLiveInvitationConsumer } from "../../hooks/usePrivateLiveInvitationConsumer";
import { useProduce } from "../../hooks/useProduce";
import usePublicLiveChat from "../../hooks/usePublicLiveChat";
import StreamView from "../StreamView";

export default defineComponent({
  name: "StreamVisitorSide",
  props: {
    userId: { type: String, required: true },
  },
  setup(props, { emit }) {
    const showGoingPrivate = ref(false);
    const streamOwner = ref<UserPublicData | null>(null);

    const {
      privateLiveInvitations,
      privateLivePrice,
      createPrivateLiveInvitation,
      setOnInvitationApprovedCb,
    } = usePrivateLiveInvitationConsumer(props.userId);

    const {
      loading,
      userList,
      joinData,
      setOnUserBroadcastingCB,
      clientServerTimeDiff,
      errorData,
    } = usePublicLiveChat({ userId: props.userId });

    const {
      consumer: privateConsumer,
      loading: privateLoading,
      joinData: privateJoinData,
      clientServerTimeDiff: privateClientServerTimeDiff,
      errorData: privateErrorData,
      isPrivateLive,
      joinPrivateLive,
      endPrivateLive,
    } = usePrivateLiveChat({ userId: props.userId });

    const getLoggerContext = (): Record<string, any> => ({
      joinData: joinData.value,
      errorData: errorData.value,
      privateErrorData: privateErrorData.value,
    });

    const consumeData = computed(() => (isPrivateLive.value ? privateJoinData.value : joinData.value));

    const {
      usersToConsume,
      connecting: consumeConnecting,
      connectingState: consumeConnectingState,
      removeUser,
      consumeStream,
      closeAllStreams,
    } = useConsume({
      joinData: consumeData,
      getLoggerContext,
    });

    const {
      produce,
      stream: visitorStream,
      constraints,
      produceStream,
      stopBroadcasting,
      onChangeDevice,
      reset,
    } = useProduce(getLoggerContext);

    const ownerIsBusy = computed(
      () => joinData.value?.users.find(i => i.userId === props.userId)?.broadcasting === false
    );

    const connecting = computed(() => Object.values(consumeConnecting.value)[0] ?? false);
    const connectingState = computed(() => Object.values(consumeConnectingState.value)[0] ?? "");

    const dateStart = computed(() => {
      if (isPrivateLive.value) {
        if (!privateJoinData.value?.me) return "";
        return new Date(
          parseISO(privateJoinData.value.me.joinTime).getTime() + privateClientServerTimeDiff.value
        ).toISOString();
      } else {
        const producer = joinData.value?.users.find(i => i.userId === props.userId) ?? null;
        if (!producer) return "";
        return new Date(parseISO(producer.joinTime).getTime() + clientServerTimeDiff.value).toISOString();
      }
    });

    const getData = async () => {
      const { result } = await providers.UserPublicDataProvider.search({
        data: { id: [props.userId] },
      });
      streamOwner.value = result[0] ?? null;
    };

    const closePrivate = () => {
      endPrivateLive();
      closeAllStreams();
      reset();
      showGoingPrivate.value = false;
    };

    const onClose = async () => {
      if (isPrivateLive.value) {
        closePrivate();
        reset();
      } else {
        emit("close");
      }
    };

    const onProduce = () => {
      privateJoinData.value && produceStream(privateJoinData.value.token);
    };

    setOnUserBroadcastingCB(({ chatUserId, broadcasting }) => {
      broadcasting && joinData.value?.token ? consumeStream(chatUserId, joinData.value.token) : closeAllStreams();
    });

    setOnInvitationApprovedCb(() => {
      showGoingPrivate.value = true;
      joinPrivateLive();
    });

    watch(joinData, (joinData, old) => {
      if (!joinData) return;
      const producer = joinData.users.find(i => i.userId === props.userId);
      const oldProducer = old?.users.find(i => i.userId === props.userId);
      if (producer?.broadcasting && !old) {
        consumeStream(producer.id, joinData.token);
      } else if (!producer && oldProducer) {
        closeAllStreams();
      }
    });

    watch(privateJoinData, async (privateJoinData, old) => {
      if (!privateJoinData) return;
      const streamingUsers = privateJoinData.users.filter(i => i.broadcasting && i.id !== privateJoinData.me.id) ?? [];
      const oldStreamingUsers = old?.users.filter(i => i.broadcasting && i.id !== old.me.id) ?? [];

      const isOwnerBroadcasting = streamingUsers.some(i => i.userId === props.userId);
      const isOldOwnerBroadcasting = oldStreamingUsers.some(i => i.userId === props.userId);

      const streamingUserIds = streamingUsers.map(i => i.id);
      const oldStreamingUserIds = oldStreamingUsers.map(i => i.id);
      if (streamingUsers.length > oldStreamingUsers.length) {
        // consume everyone who is streaming
        const ids = streamingUserIds.filter(id => !oldStreamingUserIds.includes(id));
        ids.forEach(id => consumeStream(id, privateJoinData.token));
        showGoingPrivate.value = false;
      } else if (isOldOwnerBroadcasting && !isOwnerBroadcasting) {
        // stream owner stopped streaming
        closePrivate();
      } else if (streamingUsers.length < oldStreamingUsers.length && streamingUsers.length > 0) {
        // any other user stopped streaming
        const ids = oldStreamingUserIds.filter(id => !streamingUserIds.includes(id));
        ids.forEach(id => removeUser(id));
      }
    });

    watch(privateJoinData, async (privateJoinData, old) => {
      if (!privateJoinData) return;
      if (!old && privateJoinData?.me.broadcasting) {
        stopBroadcasting(privateJoinData.token);
      }
    });

    watch(errorData, () => {
      if (!errorData) return;
      emit("error", errorData.value);
    });

    const onCreateInvitation = async (cb?: () => void) => {
      await createPrivateLiveInvitation();
      cb && cb();
    };

    getData();

    return () =>
      h(StreamView, {
        props: {
          ownerId: props.userId,
          isProducing: unref(produce),
          isOwner: false,
          usersToConsume: unref(usersToConsume),
          localStream: unref(visitorStream),
          connecting: unref(connecting),
          connectingState: unref(connectingState),
          constraints: unref(constraints),
          loading: isPrivateLive.value ? unref(privateLoading) : unref(loading),
          userList: unref(userList),
          streamStartedDate: unref(dateStart),
          threadId: joinData.value?.threadId,
          token: joinData.value?.token,
          privateThreadId: privateJoinData.value?.threadId,
          privateToken: privateJoinData.value?.token,
          privateLivePrice: unref(privateLivePrice),
          privateLiveInvitations: unref(privateLiveInvitations),
          isPrivateLive: unref(isPrivateLive),
          showGoingPrivate: unref(showGoingPrivate),
          ownerIsBusy: unref(ownerIsBusy),
          ownerUser: unref(streamOwner),
          privateConsumer: unref(privateConsumer),
        },
        on: {
          close: onClose,
          createInvitation: onCreateInvitation,
          produce: onProduce,
          changeDevice: onChangeDevice,
          joinPrivate: joinPrivateLive,
          returnToPublicLive: closePrivate,
        },
      });
  },
});
